// Цвета клиентов: именованные (ключи) и любые #rrggbb. В календаре Google всего 11 цветов — берётся ближайший.
import { GROUP_KIND, type GroupKind } from './groups.ts';

export const CLIENT_COLORS: ReadonlyArray<readonly [string, string]> = [
  ['red', 'Красный'], ['orange', 'Оранжевый'], ['yellow', 'Жёлтый'], ['green', 'Зелёный'], ['teal', 'Бирюзовый'],
  ['blue', 'Синий'], ['violet', 'Фиолетовый'], ['pink', 'Розовый'], ['brown', 'Коричневый'], ['grey', 'Серый'],
];
export const CAL_COLOR_ID: Readonly<Record<string, string>> = {
  red: '11', orange: '6', yellow: '5', green: '10', teal: '7', blue: '9', violet: '3', pink: '4', brown: '8', grey: '8',
};
export const COLOR_HEX: Readonly<Record<string, string>> = {
  red: '#dc2626', orange: '#f97316', yellow: '#eab308', green: '#16a34a', teal: '#14b8a6', blue: '#2563eb',
  violet: '#7c3aed', pink: '#db2777', brown: '#92400e', grey: '#6b7280',
};
export const COLOR_DEFAULTS = { personal: 'blue', project: 'violet' } as const;

export function colorLabel(k: string | null | undefined): string {
  return CLIENT_COLORS.find(([key]) => key === k)?.[1] ?? '';
}
export function isHexColor(k: unknown): k is string {
  return /^#[0-9a-f]{6}$/i.test(String(k ?? ''));
}
export function isColor(k: unknown): k is string {
  return typeof k === 'string' && (!!colorLabel(k) || isHexColor(k));
}

export function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const m = String(hex || '').replace('#', '');
  if (m.length !== 6) return { h: 0, s: 0, l: 50 };
  const r = parseInt(m.slice(0, 2), 16) / 255, g = parseInt(m.slice(2, 4), 16) / 255, b = parseInt(m.slice(4, 6), 16) / 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2;
  let h = 0, s = 0;
  if (mx !== mn) {
    const d = mx - mn;
    s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
    if (mx === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (mx === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return { h, s: s * 100, l: l * 100 };
}

export function hslToHex(h: number, s: number, l: number): string {
  const S = s / 100, L = l / 100, c = (1 - Math.abs(2 * L - 1)) * S, x = c * (1 - Math.abs(((h / 60) % 2) - 1)),
    m = L - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; } else if (h < 120) { r = x; g = c; } else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; } else if (h < 300) { r = x; b = c; } else { r = c; b = x; }
  const q = (v: number): string => {
    const n = Math.max(0, Math.min(255, Math.round((v + m) * 255)));
    return (n < 16 ? '0' : '') + n.toString(16);
  };
  return '#' + q(r) + q(g) + q(b);
}

// Ближайший именованный цвет — для события в календаре Google.
export function nearestColorKey(hex: string): string {
  const t = hexToHsl(hex);
  if (t.s < 12) return 'grey';
  let best = 'blue', bd = 1e9;
  for (const k of Object.keys(COLOR_HEX)) {
    if (k === 'grey') continue;
    const c = hexToHsl(COLOR_HEX[k]!);
    let d = Math.abs(c.h - t.h);
    d = Math.min(d, 360 - d);
    if (k === 'brown') d += 20;
    if (d < bd) { bd = d; best = k; }
  }
  return best;
}

export function calColorIdFor(k: string | null | undefined): string {
  if (!k) return '';
  if (CAL_COLOR_ID[k]) return CAL_COLOR_ID[k]!;
  return isHexColor(k) ? CAL_COLOR_ID[nearestColorKey(k)] ?? '' : '';
}

interface Colored { color?: string | null }
interface ColorSettings { color_personal?: string | null; color_project?: string | null }

// Действующий цвет клиента: свой → цвет группы → по умолчанию для личных групп / коммерческих проектов.
export function effColorKey(client: Colored | null, group: (Colored & { kind: GroupKind }) | null,
  settings: ColorSettings): string {
  if (client && isColor(client.color)) return client.color;
  if (group && isColor(group.color)) return group.color;
  const project = group?.kind === GROUP_KIND.PROJECT;
  const k = project ? settings.color_project : settings.color_personal;
  return isColor(k) ? k : (project ? COLOR_DEFAULTS.project : COLOR_DEFAULTS.personal);
}

// Цвет для события в календаре Google: только если выбран явно (клиент, группа или изменённый цвет по умолчанию).
export function calColorKey(client: Colored | null, group: (Colored & { kind: GroupKind }) | null,
  settings: ColorSettings): string {
  if (client && isColor(client.color)) return client.color;
  if (group && isColor(group.color)) return group.color;
  const project = group?.kind === GROUP_KIND.PROJECT;
  const k = project ? settings.color_project : settings.color_personal;
  const def = project ? COLOR_DEFAULTS.project : COLOR_DEFAULTS.personal;
  return isColor(k) && k !== def ? k : '';
}
